import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import DataTable from 'react-data-table-component';
import { getSchemeBankAccountList } from '../../../helper/Api';
import { getDepartmentDistrictFields, DepartmentDistrictBadge } from '../../Tools/DepartmentIdentityCell';
import Spinner from '../../Tools/spinner';
import './SchemeAccountList.css';

/** Match Consolidate PPA / Department list viewport fill */
const TABLE_BODY_SCROLL_HEIGHT = 'calc(72vh - 48px)';

const SchemeAccountList = ({ searchStr = '', tier = '', district_id = '' }) => {
    const { authData } = useSelector((state) => state.authData);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const customStyles = {
        headCells: {
            style: {
                fontWeight: '700',
                fontSize: '13px',
                whiteSpace: 'normal',
                overflow: 'visible',
                textOverflow: 'clip',
                lineHeight: 1.35,
                color: '#28326f',
            },
        },
        cells: {
            style: {
                fontSize: '13px',
            },
        },
    };

    const columns = [
        {
            name: 'Department',
            selector: (row) => row.department_name,
            cell: (row) => {
                const { districtName } = getDepartmentDistrictFields(row);
                return (
                    <div className="scheme-account-dept-cell">
                        <span className="scheme-account-dept-name">
                            {row.department_name || '—'}
                        </span>
                        <div className="scheme-account-dept-badges">
                            {row.department_level ? (
                                <span className="scheme-account-dept-level-badge">
                                    {row.department_level}
                                </span>
                            ) : null}
                            {districtName ? (
                                <DepartmentDistrictBadge row={row} />
                            ) : null}
                        </div>
                        {row.department_code ? (
                            <span className="scheme-account-dept-code">
                                {row.department_code}
                            </span>
                        ) : null}
                    </div>
                );
            },
            sortable: true,
            wrap: true,
            minWidth: '220px',
            grow: 1.4,
        },
        {
            name: 'Scheme Name',
            selector: (row) => row.scheme_name,
            cell: (row) => row.scheme_name || '—',
            sortable: true,
            wrap: true,
            minWidth: '240px',
            grow: 2,
        },
        {
            name: 'Scheme No',
            selector: (row) => row.scheme_code,
            cell: (row) => row.scheme_code || '—',
            sortable: true,
            wrap: true,
            minWidth: '140px',
            grow: 0.9,
        },
        {
            name: 'Account Name',
            selector: (row) => row.account_name,
            cell: (row) => row.account_name || '—',
            sortable: true,
            wrap: true,
            minWidth: '180px',
            grow: 1.2,
        },
        {
            name: 'Account No',
            selector: (row) => row.account_no,
            cell: (row) => row.account_no || '—',
            sortable: true,
            wrap: true,
            minWidth: '150px',
            grow: 1,
        },
    ];

    const handleChangeRowsPerPage = (currentRowsPerPage) => {
        setRowsPerPage(currentRowsPerPage);
        setCurrentPage(1);
    };

    const fetchList = async () => {
        try {
            setLoading(true);
            const resp = await getSchemeBankAccountList({
                searchStr: searchStr || '',
                tier: tier || '',
                department_id:
                    authData?.user?.department_id !== '1'
                        ? authData?.user?.department_id
                        : null,
                district_id: district_id || '',
            });
            if (resp?.data?.status) {
                setData(resp.data.result || []);
            } else {
                setData([]);
            }
        } catch (error) {
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchStr, tier, district_id]);

    return (
        <div className="scheme-account-datatable-root">
            {loading && <Spinner />}
            <DataTable
                customStyles={customStyles}
                className="dataTables_wrapper"
                columns={columns}
                data={data}
                progressPending={loading}
                persistTableHead
                fixedHeader
                fixedHeaderScrollHeight={TABLE_BODY_SCROLL_HEIGHT}
                pagination
                paginationPerPage={rowsPerPage}
                paginationDefaultPage={currentPage}
                paginationTotalRows={data.length}
                paginationRowsPerPageOptions={[25, 50, 100]}
                onChangeRowsPerPage={handleChangeRowsPerPage}
                onChangePage={setCurrentPage}
                noDataComponent={
                    <div className="p-4 text-muted">
                        No child scheme accounts found.
                    </div>
                }
            />
        </div>
    );
};

export default SchemeAccountList;
